import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import MemberFirearmTable from '../../../components/tables/MemberFirearmTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useMemberFirearmApi } from '../../../hooks/useMemberFirearmApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import {
  ExerciseActivityTargetDTO,
  FirearmCalibreDTO,
  FirearmMakeDTO,
  FirearmPurposeDTO,
  FirearmSpecificDTO,
  FirearmTypeDTO,
  MemberFirearmDTO,
} from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { getRoles } from '../../../utils/tokenUtils';
import { useFirearmTypes } from '../../../hooks/useFirearmTypeApi';
import { useFirearmSpecifics, useFirearmSpecificWithExercisesById } from '../../../hooks/useFirearmSpecificApi';
import { useFirearmMakes } from '../../../hooks/useFirearmMakeApi';
import { useFirearmPurposes } from '../../../hooks/useFirearmPurposeApi';
import { useFirearmCalibres } from '../../../hooks/useFirearmCalibreApi';
import MemberFirearmDataCard from '../../../components/dataCards/MemberFirearmDataCard';
import { CalendarCheck, Download, Target } from 'lucide-react';
import ButtonLegendCta from '../../../components/common/ButtonLegendCta';
import { getButtonLegendItems } from '../../../utils/buttonLegendDisplayUtils';
import FirearmOverview from '../../../components/dashboards/FirearmOverview';
import { useViewMode } from '../../../utils/viewModeUtils/useViewMode';
import { ViewToggle } from '../../../components/common/ViewToggle';
import { ViewModeConstants } from '../../../constants/ViewModeConstants';
import Drawer from '../../../components/common/Drawer';
import DownloadPostalTargets from '../../../components/drawer/DownloadPostalTargets';
import DownloadActivityTargets from '../../../components/drawer/DownloadActivityTargets';
import { useExerciseActivityTargetApi } from '../../../hooks/useExerciseActivityTargetApi';
import { useSearchState } from '../../../utils/searchStateUtils/useSearchState';
import { EndorsementLicenceTypeConstants } from '../../../constants/EndorsementLicenceTypeConstants';

const legendItems = getButtonLegendItems('memberFirearm');

interface DrawerViewTypes {
  title: string;
  viewType: 'downloadActivityTarget' | 'downloadPostalTarget';
}

const MemberFirearmList: React.FC = () => {
  const { search, remove, extract, isLoading, error } = useMemberFirearmApi();
  const [items, setItems] = useState<MemberFirearmDTO[]>([]);
  
  // Search state persistence
  const {
    currentPage,
    pageSize,
    sortBy,
    sortDescending,
    searchFilters,
    setCurrentPage,
    setPageSize,
    setSortBy,
    setSortDescending,
    setSort,
    setSearchFilters,
    showAdvanced,
    setShowAdvanced,
    clearSearchState,
  } = useSearchState('memberfirearms', {
    currentPage: 1,
    pageSize: 10,
    sortBy: 'CreatedUtc',
    sortDescending: true,
    searchFilters: {},
    showAdvanced: false,
  });
  
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedFirearmSpecificId, setSelectedFirearmSpecificId] = useState<string | undefined>(undefined);
  const [drawerView, setDrawerView] = useState<DrawerViewTypes>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activityTargets, setActivityTargets] = useState<ExerciseActivityTargetDTO[]>([]);
  const roles = getRoles();

  const { view, toggle } = useViewMode(
    'member-firearms',
    roles?.includes('User') ? ViewModeConstants.DATA_CARDS : ViewModeConstants.TABLE,
  );

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Firearms', href: '#' },
  ];

  // Cached TQ hooks for dropdown data
  const { data: firearmTypes = [], isLoading: isLoadingFirearmTypes } = useFirearmTypes();
  const { data: firearmSpecifics = [], isLoading: isLoadingFirearmSpecifics } = useFirearmSpecifics();
  const { data: firearmMakes = [], isLoading: isLoadingFirearmMakes } = useFirearmMakes();
  const { data: firearmCalibers = [], isLoading: isLoadingFirearmCalibers } = useFirearmCalibres();
  const { data: firearmPurposes = [], isLoading: isLoadingFirearmPurposes } = useFirearmPurposes();
  const { data: firearmSpecific, isLoading: isLoadingFirearmSpecific } = useFirearmSpecificWithExercisesById(selectedFirearmSpecificId);
  const { search: searchActivityTargets, isLoading: isLoadingActivityTargets } = useExerciseActivityTargetApi();

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: sortBy,
        sortDescending: sortDescending,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Member Firearms:', error);
    }
  };

  const fetchActivityTargets = async () => {
    try {
      const response = await searchActivityTargets({
        filters: {
          isActive: true,
        },
        page: 1,
        pageSize: 9999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      setActivityTargets(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching activity targets:', error);
    }
  };

  const handleSort = (sortKey: string) => {
    if (sortBy === sortKey) {
      setSort(sortKey, !sortDescending);
    } else {
      setSort(sortKey, true);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, searchFilters);
  }, [currentPage, pageSize, sortBy, sortDescending]);

  const ExportItems = async (
    page: number,
    size: number,
    filters = {},
  ): Promise<void> => {
    const searchRequest: any = {
      filters,
      page,
      pageSize: size,
      sortBy: 'CreatedUtc',
      sortDescending: true,
    };

    try {
      const response = await extract(searchRequest);
      if (response && response.success) {
        // Download the file
        // Assuming the response.data contains the URL to the exported file
        window.open(response.data, '_blank');
      }
    } catch (error) {
      console.error(`Error fetching Members:`, error);
    } finally {
    }
  };

  const handleExport = (): void => {
    setCurrentPage(1);
    ExportItems(1, 9999999, searchFilters);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    clearSearchState();
  };

  const handleDownloadPostal = (dto: MemberFirearmDTO) => {
    setSelectedFirearmSpecificId(dto.firearmSpecificId ?? '');
    setIsDrawerOpen(true);
    setDrawerView({ title: 'Postal', viewType: 'downloadPostalTarget' });
  };

  const handleSubmitPostal = (dto: MemberFirearmDTO) => {
    navigate(`/admin/scores/create?firearm=${dto.id}`);
  };

  const handleDownloadActivity = (dto?: MemberFirearmDTO) => {
    fetchActivityTargets();
    setIsDrawerOpen(true);
    setDrawerView({ title: 'Activity', viewType: 'downloadActivityTarget' });
  };

  const handleSubmitActivity = (dto: MemberFirearmDTO) => {
    navigate(`/admin/activities/create?firearm=${dto.id}`);
  };

  const handleDelete = async (dto: MemberFirearmDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, searchFilters);
    } catch (error) {
      console.error('Error deleting MemberFirearm:', error);
    }
  };

  const handleRewal = (dto: MemberFirearmDTO) => {
    navigate(`/admin/memberendorsements/create?licenceTypeId=${EndorsementLicenceTypeConstants.RENEWAL_OF_EXISTING_LICENCE}&memberFirearmId=${dto.id}`);
  };

  const handleRestore = async (dto: MemberFirearmDTO) => { };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/memberfirearms"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin', 'User']}
      requiredPermission="Manage.MemberFirearm.View"
    >
      <Card
        title="Manage Member Firearms"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex items-center gap-2 sm:gap-3">
            {roles?.includes('Admin') && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? "Hide Advanced Search" : "Advanced Search"}
              </Button>
            )}

            {roles?.includes('User')
              ? [
                <ButtonLegendCta key="legend" items={legendItems} />,
                <AddNewButton
                  key="add"
                  label='Add a Firearm'
                  onClick={() => navigate('/admin/memberfirearms/create')}
                />,
              ]
              : [
                <Button key="export" onClick={handleExport} variant="secondary">
                  Export
                </Button>,
              ]}
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          {showAdvanced && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <FormField
                id="firearmTypeId"
                label="Search by Firearm Type"
                value={searchFilters.firearmTypeId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    firearmTypeId: e.target.value,
                  })
                }
                placeholder="Select by Firearm Type"
                options={firearmTypes.map((type) => ({
                  value: type.id,
                  label: type.title || '',
                }))}
                isLoading={isLoadingFirearmTypes}
              />
              <FormField
                id="firearmSpecificId"
                label="Search by Firearm Specific"
                value={searchFilters.firearmSpecificId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    firearmSpecificId: e.target.value,
                  })
                }
                placeholder="Select Firearm Specific"
                options={firearmSpecifics.map((specific) => ({
                  value: specific.id,
                  label: specific.title || '',
                }))}
                isLoading={isLoadingFirearmSpecifics}
              />
              <FormField
                id="firearmMakeId"
                label="Search by Firearm Make"
                value={searchFilters.firearmMakeId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    firearmMakeId: e.target.value,
                  })
                }
                placeholder="Select Firearm Make"
                options={firearmMakes.map((make) => ({
                  value: make.id,
                  label: make.title || '',
                }))}
                isLoading={isLoadingFirearmMakes}
              />
              <FormField
                id="firearmCaliberId"
                label="Search by Firearm Caliber"
                value={searchFilters.firearmCaliberId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    firearmCaliberId: e.target.value,
                  })
                }
                placeholder="Select Firearm Caliber"
                options={firearmCalibers.map((caliber) => ({
                  value: caliber.id,
                  label: caliber.title || '',
                }))}
                isLoading={isLoadingFirearmCalibers}
              />
              <FormField
                id="firearmPurposeId"
                label="Search by Firearm Purpose"
                value={searchFilters.firearmPurposeId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    firearmPurposeId: e.target.value,
                  })
                }
                placeholder="Select Firearm Purpose"
                options={firearmPurposes.map((purpose) => ({
                  value: purpose.id,
                  label: purpose.title || '',
                }))}
                isLoading={isLoadingFirearmPurposes}
              />
              <FormField
                id="licenceCardNumber"
                label="Search by Licence Card Number"
                value={searchFilters.licenceCardNumber || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    licenceCardNumber: e.target.value,
                  })
                }
                placeholder="Search by Licence Card Number"
              />
              <FormField
                id="applicationNumber"
                label="Search by Application Number"
                value={searchFilters.applicationNumber || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    applicationNumber: e.target.value,
                  })
                }
                placeholder="Search by Application Number"
              />
              <FormField
                id="createdFrom"
                label="Created From"
                type="date"
                value={searchFilters.createdFrom || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdFrom: e.target.value })
                }
              />
              <FormField
                id="createdTo"
                label="Created To"
                type="date"
                value={searchFilters.createdTo || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdTo: e.target.value })
                }
              />
            </div>
          )}

          <div className="flex justify-between items-center">
            <div className="space-x-4 mb-4">
              {showAdvanced && (
                <>
                  <Button variant="secondary" onClick={handleClearSearch}>
                    Clear Search
                  </Button>
                  <Button type="submit">Search</Button>
                </>
              )}
            </div>

            <div className="hidden md:block">
              {roles?.includes('User') && (
                <ViewToggle view={view} toggle={toggle} />
              )}
            </div>
          </div>
        </form>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
          {/* Left: Overview */}

          {roles?.includes('User') && (
            <aside className="xl:col-span-3">
              <div className="xl:sticky xl:top-4">
                <FirearmOverview items={items} gridLayout="responsive" />
              </div>
            </aside>
          )}

          {/* Right: Filters + List */}
          <section className={roles?.includes('User') ? "xl:col-span-9" : "xl:col-span-12"}>
            {/* keep your search form here */}
            {(roles?.includes('Admin') || view === ViewModeConstants.TABLE) ? (
              <MemberFirearmTable
                data={items}
                handleEdit={(dto) =>
                  navigate(`/admin/memberfirearms/${dto.id}`)
                }
                handleDownloadPostal={handleDownloadPostal}
                handleSubmitPostal={handleSubmitPostal}
                handleDownloadActivity={handleDownloadActivity}
                handleSubmitActivity={handleSubmitActivity}
                handleDelete={handleDelete}
                handleRestore={handleRestore}
                currentPage={currentPage}
                totalPages={Math.ceil(totalRecords / pageSize)}
                setCurrentPage={setCurrentPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                isLoading={isLoading}
                sortBy={sortBy}
                sortDescending={sortDescending}
                onSort={handleSort}
              />
            ) : (
              <MemberFirearmDataCard
                data={items}
                handleEdit={(dto) =>
                  navigate(`/admin/memberfirearms/${dto.id}`)
                }
                handleRewal={handleRewal}
                handleDelete={handleDelete}
                currentPage={currentPage}
                totalPages={Math.ceil(totalRecords / pageSize)}
                setCurrentPage={setCurrentPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                isLoading={isLoading}
                renderSecondaryActions={(row) => (
                  <div className="flex flex-col w-full gap-3">
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-2">Postal</div>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          className="w-full"
                          variant="secondary"
                          icon={Download}
                          onClick={() => handleDownloadPostal(row)}
                        > Download
                        </Button>
                        <Button
                          className="w-full"
                          variant="secondary"
                          icon={Target}
                          onClick={() => handleSubmitPostal(row)}
                        > Submit
                        </Button>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-2">Activity</div>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          className="w-full"
                          variant="secondary"
                          icon={Download}
                          onClick={() => handleDownloadActivity()}
                        >
                          Download
                        </Button>
                        <Button
                          className="w-full"
                          variant="secondary"
                          icon={Target}
                          onClick={() => handleSubmitActivity(row)}
                        >
                          Submit
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              />
            )}
          </section>
        </div>

        <Drawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          title={`Download ${drawerView?.title} Target`}
        >
          {drawerView?.viewType === 'downloadPostalTarget' && (
            <DownloadPostalTargets
              firearmSpecific={firearmSpecific}
              isLoading={isLoadingFirearmSpecific}
              onClose={handleDrawerClose}
            />
          )}
          {drawerView?.viewType === 'downloadActivityTarget' && (
            <DownloadActivityTargets
              activityTargets={activityTargets}
              isLoading={isLoadingActivityTargets}
              onClose={handleDrawerClose}
            />
          )}
        </Drawer>
      </Card>
    </AdminLayout>
  );
};

export default MemberFirearmList;

